import { useAuth0 } from "@auth0/auth0-react";
import { useRef, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FinicityConnect } from '@finicity/connect-web-sdk';
import { OBCustomerContext } from '../../contexts/ob-customer-context';
import {
    generateAppToken,
    getInternalOBCustomerAccounts
} from "../../utilities/ob-utils";

import {
    svc_ob_genConnectURL,
    svc_ob_getCustomerAccounts
} from '../../services/external-openbanking.service';

import {
    svc_createOBCustomerAccount,
    svc_updateOBCustomerAccount
} from '../../services/internal-openbanking.service';

const ConnectOBAccount = ({
    launchConnect = null
}) => {
    const { user, getAccessTokenSilently } = useAuth0();
    const {
        obAppToken,
        setOBAppToken,
        obCustomer,
        updateOBCustomer
    } = useContext(OBCustomerContext);
    const navigate = useNavigate();
    const btnRef = useRef(null);

    let connectUrl = '';

    const handleConnectClick = async (e) => {
        e?.preventDefault();

        if (obAppToken && obCustomer) {
            generateConnectUrl(obAppToken, obCustomer);
        }
    }

    const generateConnectUrl = async (token, customer) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_ob_genConnectURL(accessToken, token, customer?.obId);

        if (error) {
            if (parseInt(error.statusCode) === 401) {
                const updatedToken = await generateAppToken(accessToken);
                setOBAppToken(updatedToken);
                updateOBCustomer(updatedToken);
                await generateConnectUrl(updatedToken, customer);
                return;
            }

            console.error('error', error);
            throw new Error(error);
        }

        connectUrl = data.link;

        if (typeof window !== 'undefined') {
            try {
                FinicityConnect.destroy();
            } catch (error) {
                console.log('No existing Finicity Connect iframe to destroy');
            }

            const connectEventHandlers = {
                onDone: (event) => {
                },
                onCancel: (event) => {
                },
                onError: (event) => {
                },
                onUser: ({ action, code, customerId }) => {
                    if (
                        action === 'End' &&
                        code === 200 &&
                        customerId
                    ) {
                        getAccounts(customerId)
                            .then(async () => {
                                const accessToken = await getAccessTokenSilently();

                                const onFailure = async (error) => {
                                    if (parseInt(error.statusCode) === 401) {
                                        const accessToken = await getAccessTokenSilently();
                                        const updatedToken = await generateAppToken(accessToken);
                                        setOBAppToken(updatedToken);
                                        updateOBCustomer(updatedToken);
                                        getInternalOBCustomerAccounts(obCustomer, accessToken, updatedToken, user.sub, null);
                                        return;
                                    }
                                }

                                getInternalOBCustomerAccounts(
                                    obCustomer,
                                    accessToken,
                                    user.sub,
                                    null,
                                    onFailure
                                ).then(() => {
                                    navigate('/accounts');
                                }).catch((error) => {
                                    console.error('error', error);
                                });
                            });
                    }
                },
                onLoad: () => {
                },
            };

            const connectOptions = {
                overlay: 'rgba(199,201,199, 0.5);z-index: 10000;',
            };

            FinicityConnect.launch(
                connectUrl,
                connectEventHandlers,
                connectOptions
            );
        }
    };

    const getAccounts = async (
        customerId,
        appToken = obAppToken
    ) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_ob_getCustomerAccounts(accessToken, appToken, customerId);

        if (error) {
            // Retry with new token if 401
            if (parseInt(error.statusCode) === 401) {
                const updatedToken = await generateAppToken(accessToken);
                setOBAppToken(updatedToken);
                updateOBCustomer(updatedToken);
                getAccounts(customerId, updatedToken);
                return;
            }

            console.error('error', error);
            throw new Error(error);
        }

        // Update or create accounts
        const freshAccounts = data.accounts;
        createOrUpdateAccounts(freshAccounts);
    };

    const createOrUpdateAccounts = async (freshAccounts) => {
        const accessToken = await getAccessTokenSilently();
        const currentAccounts = await getInternalOBCustomerAccounts(
            obCustomer,
            accessToken,
            user.sub
        );
        const accountsToCreate = [];
        const accountsToUpdate = [];

        freshAccounts.forEach((freshAccount) => {
            const existingAccount = currentAccounts.find(
                (obAccount) => obAccount.accountId === parseInt(freshAccount.id)
            );

            if (existingAccount) {
                accountsToUpdate.push({
                    existingAccount,
                    freshAccount
                });
            } else {
                accountsToCreate.push(freshAccount);
            }
        });

        const createPromises = accountsToCreate.map((account) => {
            return svc_createOBCustomerAccount(accessToken, user.sub, {
                obCustomerId: obCustomer.id,
                accountId: parseInt(account.id),
                data: account,
                includeBalance: false,
            });
        });

        const updatePromises = accountsToUpdate.map((account) => {
            return svc_updateOBCustomerAccount(accessToken, user.sub, {
                obCustomerId: obCustomer.id,
                accountId: parseInt(account.existingAccount.accountId),
                data: account.freshAccount,
                includeBalance: account.existingAccount.includeBalance,
            });
        });

        await Promise.all(createPromises);
        await Promise.all(updatePromises);
        await getInternalOBCustomerAccounts(obCustomer, accessToken, user.sub, null);
    };

    useEffect(() => {
        if (launchConnect === 'openbanking') {
            handleConnectClick({
                preventDefault: () => {}
            });
        }
    }, [launchConnect, obAppToken, obCustomer]);

    return (
        <button className="button button-blue button-slim" onClick={handleConnectClick} ref={btnRef}>
            Connect Your Accounts
        </button>
    );
}

export default ConnectOBAccount;
